import {errorToString, MiniEvent} from "@cdx/common";
import {matchPath} from "react-router";
import cdxEnv from "../env";
import {appMode, isOpenDecks} from "./app-mode";

export type ApiErrorEvent = {
  type: string;
  status?: number;
  statusText?: string;
  message?: unknown;
  value?: string;
};
export const apiErrorEvent = new MiniEvent<ApiErrorEvent | null>();

export const API_ERRORS = {
  API_VERSION_MISMATCH: "api-version-mismatch",
  API_ERROR: "apiError",
  FETCH_ERROR: "fetchError",
  NEW_SUBDOMAIN: "newSubdomain",
};

let inQueue = 0;
export const smartFetch = <T>(
  rawUrl: string,
  {query, ...opts}: RequestInit & {query?: Record<string, any>}
): Promise<T> => {
  const buildUrl = () => {
    if (!query || Object.keys(query).length === 0) {
      return rawUrl;
    }
    const searchParams = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      searchParams.append(key, String(value));
    });
    return `${rawUrl}?${searchParams.toString()}`;
  };
  const url = buildUrl();
  const doFetch = (): Promise<T> => {
    return fetch(url, opts).then((res) => {
      if (res.status === 429) {
        const retrySecs = res.headers.get("x-ratelimit-reset");
        const secs = retrySecs ? parseInt(retrySecs, 10) : 3;
        if (inQueue > 20) {
          return Promise.reject({
            type: API_ERRORS.API_ERROR,
            status: 429,
            statusText: "Too many requests",
            message: "You are being rate limited. Please slow down a bit.",
          });
        } else {
          inQueue += 1;
          return new Promise((resolve, reject) => {
            setTimeout(
              () => {
                doFetch()
                  .then(resolve, reject)
                  .finally(() => {
                    inQueue += -1;
                  });
              },
              secs * 1000 + (100 + Math.random() * 250) * (inQueue + 1)
            );
          });
        }
      } else {
        const type = res.headers.get("Content-Type");
        const getData = () => {
          if (type?.startsWith("application/json")) return res.json();
          if (type?.startsWith("application/octet-stream")) return res.bytes();
          return res.text();
        };
        if (res.status >= 400) {
          return getData().then((body) =>
            Promise.reject({
              type: API_ERRORS.API_ERROR,
              status: res.status,
              statusText: res.statusText,
              message: res.status === 400 ? body : errorToString(body),
            })
          );
        } else {
          const newSubdomain = res.headers.get("X-New-Subdomain");
          if (newSubdomain) {
            apiErrorEvent.emit({type: API_ERRORS.NEW_SUBDOMAIN, value: newSubdomain});
          }
          return getData();
        }
      }
    });
  };
  return doFetch().catch((error) => {
    // Preserve aborts (callers detect them via `error.name === "AbortError"`) and any rejection
    // we already normalized above (rate-limit / HTTP-error objects carry a `type`).
    if (error && (error.name === "AbortError" || error.type)) return Promise.reject(error);
    // Otherwise this is a genuine transport failure: `fetch` rejects with a bare `TypeError`
    // ("Failed to fetch") that has no `type`, which otherwise collapses to "Unknown error:
    // undefined" in the toast layer. Tag it as a connectivity issue so it surfaces the curated
    // "Could not reach the server" indicator instead.
    return Promise.reject({type: API_ERRORS.FETCH_ERROR, error});
  });
};

const extractAccountRegExp = cdxEnv.EXTRACT_SUBDOMAIN_REGEXP
  ? new RegExp(cdxEnv.EXTRACT_SUBDOMAIN_REGEXP)
  : null;

const isAccountlessMode = () => appMode !== "app";

const getAccountFromHostname = () => {
  if (isAccountlessMode()) return null;
  const match = extractAccountRegExp && window.location.hostname.match(extractAccountRegExp);
  return match ? match[1] : null;
};

/**
 * Returns the org that only the config names — the `DEFAULT_ACCOUNT` dev and on-premise setups,
 * whose hostname carries no subdomain for the api to read off `Origin`. A caller that the api can
 * already place by `Origin` asks this instead of {@link getCurrentAccount}, and names the org only
 * when it answers non-null.
 */
export const getAccountFromConfig = () =>
  isAccountlessMode() ? null : cdxEnv.DEFAULT_ACCOUNT || null;

export const getCurrentAccount = () => getAccountFromHostname() ?? getAccountFromConfig();

const getPublicPath = () => {
  if (!isOpenDecks) return null;
  const match = matchPath<{publicPath: string}>(window.location.pathname, {path: "/:publicPath"});
  return match && match.params.publicPath;
};

export const parseParams = (search: string): Record<string, string> => {
  return Object.fromEntries(new URLSearchParams(search));
};

export const stringifyParams = (params: Record<string, any>): string => {
  return new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined))
  ).toString();
};

// Lets the api tell which releases still read a deprecated field. Left out of Open Decks: some of
// its GETs carry no other custom header, and this one would cost them a CORS preflight.
const releaseHeader = (): Record<string, string> =>
  appMode === "app" && cdxEnv.DEPLOYMENT_LABEL
    ? {"X-Codecks-Release": cdxEnv.DEPLOYMENT_LABEL}
    : {};

export const getDefaultHeaders = (): Record<string, string> => {
  if (isOpenDecks) {
    const publicPath = getPublicPath();
    return publicPath ? {"X-Public-Path": publicPath} : {};
  } else {
    const subdomain = getCurrentAccount();
    return {...releaseHeader(), ...(subdomain ? {"X-Account": subdomain} : {})};
  }
};
